import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

// One row of pricing cards drifting right-to-left forever, at a constant
// speed, with EACH CARD IN THE DOM ONCE. There is no cloned second copy of
// the row: every card is positioned on its own (translate3d) along a loop
// as long as the whole row - each card's measured width plus the gap - and
// the moment a card is fully past the left edge its position wraps to just
// behind the last card on the right.
//
// For that to never leave a hole or show a card appearing, the visible
// window must be shorter than the loop by the widest card and two gaps:
// W <= loop - widest - 2 * gap (n - 1 cards wide, for equal cards). The
// frame is sized to that (--plan-window-max) rather than stretching the
// cards, so cards keep their own width at every breakpoint.
//
// It eases to a stop, and back to speed, while:
//   * the pointer is over the cards (mouse / pen)
//   * keyboard focus is inside a card - the focused card is also brought
//     fully into view
//   * a finger is on the cards, and for a moment after, so a plan never
//     slides away mid-tap
//   * the visitor is dragging the row
//   * the frame is off-screen (the animation loop is not running at all)
// It moves for every visitor, whatever their OS motion setting; the row
// can also be dragged (or scrolled sideways with a trackpad / shift+wheel).
// With too few plans to loop (< 3) it is a still, centred single row.

const SPEED = 30; // px per second
const EASE_SECONDS = 0.2; // how quickly it eases to a stop / back to speed
const EDGE = 28; // the fade at each edge; a focused card is kept inside it
const MAX_STEP = 0.05; // longest frame step, so a stalled tab never jumps
const TOUCH_HOLD_MS = 2500; // stays still this long after a finger lifts
const DRAG_THRESHOLD = 6; // px of movement before a press becomes a drag
const MIN_LOOP_ITEMS = 3;

function PricingMarquee({ items, getKey, renderItem, label = 'Pricing plans' }) {
  const count = items.length;
  const isStatic = count < MIN_LOOP_ITEMS;

  const frameRef = useRef(null);
  const windowRef = useRef(null);
  const slotRefs = useRef([]);
  // widths/bases: each card's measured width and its start along the loop.
  const motion = useRef({
    offset: -EDGE,
    velocity: SPEED,
    last: 0,
    frame: 0,
    widths: [],
    bases: [],
    gap: 0,
    loop: 0,
    view: 0,
  });
  const holds = useRef({
    hover: false,
    focus: false,
    touch: false,
    drag: false,
    inView: true,
  });
  const touchTimer = useRef(0);
  // An in-progress press: where it started, and whether it became a drag.
  const press = useRef(null);
  const suppressClick = useRef(false);

  const itemKeys = items.map(getKey).join('|');

  // Where card `index` sits right now: in [-(width + gap), loop - width - gap),
  // so it wraps only once it is a full gap past the left edge.
  const positionOf = useCallback((index) => {
    const m = motion.current;
    const span = m.widths[index] + m.gap;
    let x = (m.bases[index] - m.offset) % m.loop;
    if (x < 0) x += m.loop;
    if (x >= m.loop - span) x -= m.loop;
    return x;
  }, []);

  const place = useCallback(() => {
    const m = motion.current;
    if (m.loop <= 0) return;
    m.bases.forEach((_, index) => {
      const slot = slotRefs.current[index];
      if (slot) slot.style.transform = `translate3d(${positionOf(index).toFixed(2)}px, 0, 0)`;
    });
  }, [positionOf]);

  // Moves the row by hand (drag, wheel, focus), wrapping like the loop does.
  const shift = useCallback(
    (delta) => {
      const m = motion.current;
      if (m.loop <= 0) return;
      m.offset = (((m.offset + delta) % m.loop) + m.loop) % m.loop;
      place();
    },
    [place]
  );

  const canMove = () => !isStatic && motion.current.loop > 0;
  const held = () => {
    const h = holds.current;
    return h.hover || h.focus || h.touch || h.drag;
  };

  const tick = useCallback(
    (now) => {
      const m = motion.current;
      const dt = Math.min((now - m.last) / 1000, MAX_STEP);
      m.last = now;
      const target = held() ? 0 : SPEED;
      m.velocity += (target - m.velocity) * (1 - Math.exp(-dt / EASE_SECONDS));
      m.offset = (m.offset + m.velocity * dt) % m.loop;
      place();
      if (target === 0 && m.velocity < 0.5) {
        // Fully stopped: let the loop rest until something resumes it.
        m.velocity = 0;
        m.frame = 0;
        return;
      }
      m.frame = requestAnimationFrame(tick);
    },
    // held reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [place]
  );

  const stop = useCallback(() => {
    if (motion.current.frame) cancelAnimationFrame(motion.current.frame);
    motion.current.frame = 0;
  }, []);

  // Start, keep or stop the loop to match the current holds.
  const sync = useCallback(() => {
    const m = motion.current;
    if (!canMove() || !holds.current.inView) {
      stop();
      return;
    }
    if (m.frame) return;
    if (held() && m.velocity === 0) return;
    m.last = performance.now();
    m.frame = requestAnimationFrame(tick);
    // canMove/held read refs and the render's isStatic.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, stop, isStatic]);

  // Card widths and the gap come from CSS (they change per breakpoint); the
  // loop and the window's maximum follow from them, then every card is placed.
  const measure = useCallback(() => {
    const frame = frameRef.current;
    const view = windowRef.current;
    if (!frame || !view) return;
    const m = motion.current;
    const slots = slotRefs.current.slice(0, count);
    if (isStatic || slots.some((slot) => !slot)) {
      frame.style.removeProperty('--plan-window-max');
      slots.forEach((slot) => {
        if (slot) slot.style.transform = '';
      });
      m.loop = 0;
      return;
    }
    // The track is a single grid cell all cards share, so its column-gap is
    // unused; the gap between cards is the CSS variable.
    m.gap = parseFloat(getComputedStyle(frame).getPropertyValue('--plan-gap')) || 0;
    m.widths = slots.map((slot) => slot.getBoundingClientRect().width);
    if (m.widths.some((width) => width <= 0)) {
      m.loop = 0;
      return;
    }
    let cursor = 0;
    m.bases = m.widths.map((width) => {
      const base = cursor;
      cursor += width + m.gap;
      return base;
    });
    m.loop = cursor;
    const widest = Math.max(...m.widths);
    frame.style.setProperty('--plan-window-max', `${Math.floor(m.loop - widest - 2 * m.gap)}px`);
    m.view = view.clientWidth;
    m.offset %= m.loop;
    place();
  }, [isStatic, count, place]);

  useLayoutEffect(() => {
    slotRefs.current.length = count;
    measure();
    sync();
  }, [measure, sync, count, itemKeys]);

  // Size changes (breakpoints change the card width, content can change a
  // card's height), visibility, and sideways wheel / trackpad scrolling.
  useEffect(() => {
    const frame = frameRef.current;
    const view = windowRef.current;
    if (!frame || !view) return undefined;
    let pending = 0;
    const onResize = () => {
      cancelAnimationFrame(pending);
      pending = requestAnimationFrame(() => {
        measure();
        sync();
      });
    };
    window.addEventListener('resize', onResize);
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onResize);
      if (frame.parentElement) resizeObserver.observe(frame.parentElement);
      slotRefs.current.forEach((slot) => slot && resizeObserver.observe(slot));
    }

    const visibilityObserver =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(([entry]) => {
            holds.current.inView = entry.isIntersecting;
            sync();
          })
        : null;
    if (visibilityObserver) visibilityObserver.observe(frame);

    // Only a mostly-horizontal wheel moves the row; vertical page scrolling
    // over the cards is left alone. Not passive, so it can claim the gesture.
    const onWheel = (event) => {
      const dx = event.deltaX || (event.shiftKey ? event.deltaY : 0);
      if (!dx || Math.abs(dx) < Math.abs(event.shiftKey ? 0 : event.deltaY)) return;
      event.preventDefault();
      shift(dx);
    };
    view.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      cancelAnimationFrame(pending);
      window.removeEventListener('resize', onResize);
      view.removeEventListener('wheel', onWheel);
      if (resizeObserver) resizeObserver.disconnect();
      if (visibilityObserver) visibilityObserver.disconnect();
    };
  }, [measure, sync, shift, itemKeys]);

  useEffect(
    () => () => {
      stop();
      clearTimeout(touchTimer.current);
    },
    [stop]
  );

  const onPointerEnter = (event) => {
    if (event.pointerType === 'touch') return;
    holds.current.hover = true;
    sync();
  };

  const onPointerLeave = (event) => {
    if (event.pointerType === 'touch') return;
    holds.current.hover = false;
    sync();
  };

  const onPointerDown = (event) => {
    if (!canMove() || (event.pointerType === 'mouse' && event.button !== 0)) return;
    if (event.pointerType === 'touch') {
      clearTimeout(touchTimer.current);
      holds.current.touch = true;
      sync();
    }
    press.current = { id: event.pointerId, x: event.clientX, offset: motion.current.offset, dragging: false };
  };

  const onPointerMove = (event) => {
    const p = press.current;
    if (!p || p.id !== event.pointerId) return;
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      // Released outside the row before it became a drag.
      press.current = null;
      return;
    }
    const dx = event.clientX - p.x;
    if (!p.dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      p.dragging = true;
      holds.current.drag = true;
      sync();
      if (event.currentTarget.setPointerCapture) event.currentTarget.setPointerCapture(event.pointerId);
    }
    const m = motion.current;
    m.offset = p.offset;
    shift(-dx);
  };

  const endPress = (event) => {
    const p = press.current;
    if (p && p.id === event.pointerId) {
      // A drag that ends over a plan button must not also click it.
      suppressClick.current = p.dragging;
      press.current = null;
      holds.current.drag = false;
    }
    if (event.pointerType === 'touch') {
      clearTimeout(touchTimer.current);
      touchTimer.current = setTimeout(() => {
        holds.current.touch = false;
        sync();
      }, TOUCH_HOLD_MS);
    }
    sync();
  };

  const onClickCapture = (event) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  // Keyboard focus moving into a card: hold, and bring that card fully into
  // view. A mouse click also focuses a link, but must not hold the row once
  // the pointer has left - so only :focus-visible (keyboard) focus counts.
  const onFocus = (event) => {
    let keyboard = true;
    try {
      keyboard = event.target.matches(':focus-visible');
    } catch {
      // A browser without :focus-visible: treat every focus as keyboard.
    }
    if (!keyboard) return;
    holds.current.focus = true;
    sync();
    if (!canMove()) return;
    const index = slotRefs.current.findIndex((slot) => slot && slot.contains(event.target));
    if (index < 0) return;
    const m = motion.current;
    const width = m.widths[index];
    const x = positionOf(index);
    const low = Math.min(EDGE, Math.max(0, (m.view - width) / 2));
    const high = m.view - width - low;
    if (x < low) shift(x - low);
    else if (x > high) shift(x - high);
  };

  const onBlur = (event) => {
    if (windowRef.current && windowRef.current.contains(event.relatedTarget)) return;
    holds.current.focus = false;
    sync();
  };

  // overflow: clip keeps the window from scrolling when a hidden card takes
  // focus; this undoes it on browsers that only know overflow: hidden.
  const onScroll = (event) => {
    if (event.currentTarget.scrollLeft !== 0) event.currentTarget.scrollLeft = 0;
  };

  return (
    <div className={`plan-marquee${isStatic ? ' is-static' : ''}`}>
      <div className="plan-marquee-frame" ref={frameRef} role="region" aria-label={label}>
        <div
          className="plan-marquee-window"
          ref={windowRef}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPress}
          onPointerCancel={endPress}
          onClickCapture={onClickCapture}
          onDragStart={(event) => event.preventDefault()}
          onFocus={onFocus}
          onBlur={onBlur}
          onScroll={onScroll}
        >
          <div className="plan-marquee-track">
            {items.map((item, index) => (
              <div
                key={getKey(item)}
                className="plan-marquee-slot"
                ref={(el) => {
                  slotRefs.current[index] = el;
                }}
              >
                {renderItem(item)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingMarquee;
